import * as Sentry from '@sentry/react';
import { Auth, Storage } from 'aws-amplify';
import Axios, { AxiosRequestConfig } from 'axios';
import posthog from 'posthog-js';
import { Dispatch } from 'redux';

import { notify } from 'src/clients/ApiService';
import {
  containsPathSeparator,
  FOLDER_FILE_KEY,
  replacePathSeparators,
} from 'src/constants/fileConsts';
import { UploadProgressProps } from 'src/hooks/useProgressUpload';
import { FileSaveModel } from 'src/store/files/types';
import { API } from 'src/utils/AmplifyApiUtils';

import { reportException } from './errorReporting';
import { ensureError } from './Errors';

interface SignedUploadResult {
  Location: string;
}

const FILE_UPLOAD_ERRORS = ['AWSS3ProviderManagedUpload', 'Network Error'];

const handleFileUploadFailure = (error: unknown, dispatch?: Dispatch): void => {
  const err = ensureError(error);

  reportException(err, {
    tags: { feature: 'file_upload' },
  });
  posthog.capture('file_upload_failed', {
    error_message: err.message,
    error_name: err.name,
  });

  // check if the error is a file upload then clear the progress snackbar
  // and show an error message
  if (FILE_UPLOAD_ERRORS.includes(err.message)) {
    if (dispatch) {
      notify({
        status: 'error',
        errorMessage: 'File upload failed. Please try again.',
        dispatch,
      });
    }
  }

  console.error('Uncaught error during handleFileUploadFailure:', err.message);
};

export type FileWithPath = File & { path: string };

function isFileWithPath(file: File | FileWithPath): file is FileWithPath {
  return 'path' in file;
}

export interface UploadResponse {
  ETag?: string;
  Location?: string;
  key?: string;
  Bucket?: string;
}

export interface UploadFilesToS3Input {
  acceptedFiles: any;
  targetPath?: string;
  startUploadFiles: () => void;
  exitUploadFiles: () => void;
  setUploadProgress: (fileProgress: UploadProgressProps) => void;
  filePathToS3?: string;
  modifiedFolderUploadBasePath?: string;
  ignoreFolders?: boolean;
  isUserFiles?: boolean;
  dispatch?: Dispatch;
}

interface ProgressData {
  loaded: number;
  totalSize: number;
  total?: number;
}

export type S3File = File | FileWithPath;

/**
 * get all the directories possible for a filepath "A/B/C" return "A", "A/B", "A/B/C"
 * @param targetPath prefix to each of the subpaths
 * @param filePath full filepath of an uploaded file
 * */
export const getAllSubpaths = (targetPath: string, filePath: string) => {
  const pathParts: string[] = filePath.split('/');
  const subpaths: string[] = [];
  for (let idx = 0; idx < pathParts.length; idx += 1) {
    if (idx > 0) {
      subpaths.push(
        `${targetPath ? `${targetPath}/` : ''}${pathParts
          .slice(0, idx)
          .join('/')}`,
      );
    }
  }
  return subpaths;
};

/**
 * get a finalized list of items ready to be uploaded as FILE_METADATA objects in the DB based on this S3 upload
 * @param uploadedFiles as a result of S3 upload action
 * @param folderPaths determined from properties on File objects input by the user
 * @param targetPath a prefix path for all files undergoing this given action
 * */
export const getFileMetadataObjects = (
  uploadedFiles: Awaited<
    ReturnType<typeof S3Utils.uploadFile | typeof S3Utils.uploadPortalFile>
  >[],
  folderPaths: string[],
  targetPath: string,
) => {
  const formattedTargetPath =
    targetPath && targetPath.endsWith('/')
      ? targetPath.replace(/\/$/, '')
      : targetPath;
  const allPaths = new Set<string>(); // will contain a set of strings to be used to create placeholder folder nodes included in API request
  const formattedUploadedFiles = uploadedFiles.map((file, idx) => {
    let filePathParts: string[] = [];
    if (formattedTargetPath) {
      filePathParts.push(formattedTargetPath);
    }
    if (folderPaths[idx]) {
      // if webkitRelativePath/path property is present, this should be a folder upload and we need to check if path property needs additions
      const directoryPath: string = folderPaths[idx]
        .split('/')
        .slice(0, -1)
        .join('/');
      if (directoryPath) {
        // this "if" conditional will be entered if the file object that has been uploaded had a "path" property from being inside a folder when being uploaded
        filePathParts = filePathParts.concat(directoryPath.split('/'));
        // add the full path on this file to the set of "allPaths" to help create folder placeholder objects later
        allPaths.add(
          `${
            formattedTargetPath ? `${formattedTargetPath}/` : ''
          }${directoryPath}`,
        );
      }
      // add all possible path combinations for this given file to the set of strings
      getAllSubpaths(formattedTargetPath, directoryPath).forEach(
        (subPath: string) => {
          allPaths.add(subPath);
        },
      );
    }
    return {
      ...file,
      path: filePathParts.join('/'),
    };
  });
  // for all relative paths, we need to create a file metadata object in database to represent the folder node
  allPaths.forEach((relPath) =>
    formattedUploadedFiles.push({ key: FOLDER_FILE_KEY, path: relPath }),
  );
  return formattedUploadedFiles;
};

/**
 * in a folder upload, get an ordered list of the relative folder paths associated with each uploaded file
 * @param files if originating from a file upload they will have either path or webkitrelativepath set
 * */
export const getFilePathList = (
  files: S3File[],
  modifiedFolderUploadBasePath: string,
  ignoreFolders: boolean,
): string[] =>
  files.map((file) => {
    let originalPath = '';
    if (!ignoreFolders) {
      originalPath = isFileWithPath(file)
        ? file.path.substring(1)
        : file.webkitRelativePath;
    }

    if (originalPath && modifiedFolderUploadBasePath) {
      return [modifiedFolderUploadBasePath]
        .concat(originalPath.split('/').slice(1))
        .join('/');
    }
    return originalPath;
  });

// A slash inside the browser file name is not a folder the user created.
// Drop the relative-path tail that is just that name split on `/`, so the
// upload stays in the real parent folder.
const uploadDirectoryPath = (
  relativePath: string,
  fileName: string,
): string => {
  if (!relativePath) {
    return '';
  }
  const parts = relativePath.split('/').filter((part) => part !== '');
  if (containsPathSeparator(fileName)) {
    const nameParts = fileName.split(/[/\\]/).filter((part) => part !== '');
    const tail = parts.slice(-nameParts.length).join('/');
    const normalizedName = fileName.replace(/\\/g, '/');
    if (nameParts.length > 0 && tail === normalizedName) {
      parts.splice(parts.length - nameParts.length, nameParts.length);
    } else if (parts.length > 0) {
      parts.pop();
    }
  } else if (parts.length > 0) {
    parts.pop();
  }
  return parts.join('/');
};

// uniqueUploadLeafName keeps two files in one upload from sharing a path after
// `/` and `\` are rewritten to `-`. The byte upload matches records by that
// path, so a shared path would store the first file's contents twice.
const uniqueUploadLeafName = (
  folderPath: string,
  leafName: string,
  usedPaths: Set<string>,
): string => {
  const fullPath = (name: string) =>
    folderPath ? `${folderPath}/${name}` : name;
  if (!usedPaths.has(fullPath(leafName))) {
    usedPaths.add(fullPath(leafName));
    return leafName;
  }
  const dot = leafName.lastIndexOf('.');
  const base = dot > 0 ? leafName.slice(0, dot) : leafName;
  const ext = dot > 0 ? leafName.slice(dot) : '';
  for (let n = 1; ; n += 1) {
    const candidate = `${base} (${n})${ext}`;
    if (!usedPaths.has(fullPath(candidate))) {
      usedPaths.add(fullPath(candidate));
      return candidate;
    }
  }
};

export type PreparedChannelUpload = {
  models: FileSaveModel[];
  adjustedLeafCount: number;
};

/**
 * Build FILE_METADATA create payloads for a channel upload without putting
 * bytes in S3 yet. Document rows use an empty key and an absolute path
 * (folder/filename) so CreateFileMetadata runs FormatPathAndKey and returns
 * uploadUrl — same pathway as platform-api.
 *
 * `/` and `\` in the leaf file name are rewritten to a hyphen. Slashes in the
 * relative folder path are left alone — those are real folders.
 */
export const prepareChannelUploadModels = ({
  acceptedFiles,
  targetPath = '',
  modifiedFolderUploadBasePath = '',
  ignoreFolders = false,
}: {
  acceptedFiles: any;
  targetPath?: string;
  modifiedFolderUploadBasePath?: string;
  ignoreFolders?: boolean;
}): PreparedChannelUpload => {
  let files: File[] = [];
  if (acceptedFiles?.target?.files) {
    files = [...acceptedFiles.target.files];
  } else if (Array.isArray(acceptedFiles)) {
    files = acceptedFiles;
  }
  files = files.filter((f) => !f.name.startsWith('.'));
  if (files.length === 0) {
    return { models: [], adjustedLeafCount: 0 };
  }

  const folderPaths = getFilePathList(
    files,
    modifiedFolderUploadBasePath,
    ignoreFolders,
  );
  const formattedTargetPath =
    targetPath && targetPath.endsWith('/')
      ? targetPath.replace(/\/$/, '')
      : targetPath;
  const allPaths = new Set<string>();
  const usedPaths = new Set<string>();
  let adjustedLeafCount = 0;
  const models: FileSaveModel[] = files.map((file, idx) => {
    let filePathParts: string[] = [];
    if (formattedTargetPath) {
      filePathParts.push(formattedTargetPath);
    }
    if (folderPaths[idx]) {
      const directoryPath = uploadDirectoryPath(folderPaths[idx], file.name);
      if (directoryPath) {
        filePathParts = filePathParts.concat(directoryPath.split('/'));
        allPaths.add(
          `${
            formattedTargetPath ? `${formattedTargetPath}/` : ''
          }${directoryPath}`,
        );
      }
      getAllSubpaths(formattedTargetPath, directoryPath).forEach(
        (subPath: string) => {
          allPaths.add(subPath);
        },
      );
    }
    const folderPath = filePathParts.join('/');
    const sanitizedLeaf = replacePathSeparators(file.name);
    if (sanitizedLeaf !== file.name) {
      adjustedLeafCount += 1;
    }
    const leafName = uniqueUploadLeafName(folderPath, sanitizedLeaf, usedPaths);
    const absolutePath = folderPath ? `${folderPath}/${leafName}` : leafName;
    return {
      key: '',
      path: absolutePath,
      file,
    };
  });

  allPaths.forEach((relPath) =>
    models.push({ key: FOLDER_FILE_KEY, path: relPath }),
  );
  return { models, adjustedLeafCount };
};

export const getFileSaveModelDisplayName = (file: FileSaveModel): string => {
  if (file.key === FOLDER_FILE_KEY) {
    return file.path.split('/').pop() || 'item';
  }
  if (file.key) {
    return file.key.split('/').pop() || 'item';
  }
  return file.path.split('/').pop() || 'item';
};

export const getFileSaveModelFolderPath = (file: FileSaveModel): string => {
  if (file.key === FOLDER_FILE_KEY || file.key) {
    return file.path;
  }
  const parts = file.path.split('/');
  return parts.slice(0, -1).join('/');
};

/**
 * PUT file bytes to a CreateFileMetadata uploadUrl (platform-api pathway).
 */
export const uploadToFileUploadUrl = async ({
  uploadUrl,
  file,
  onProgress,
}: {
  uploadUrl: string;
  file: File;
  onProgress?: (loaded: number, total: number) => void;
}): Promise<void> => {
  await Axios.put(uploadUrl, file, {
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        onProgress(progressEvent.loaded, progressEvent.total);
      }
    },
  });
};

export default class S3Utils {
  static uploadAdCreative(
    userId: string,
    campaignName: string,
    fileWithType: { file: File; format: string },
  ) {
    const fileKey = `${userId}/${campaignName}/${fileWithType.format
      .split('/')[0]
      .replace(/\s/g, '')}/${fileWithType.file.name}`;

    return this.uploadFile(fileKey, fileWithType.file, {});
  }

  static uploadPackageAttachment(attachmentField: string, file: File) {
    const fileKey = `${attachmentField}/${file.name}`;

    return this.uploadFile(fileKey, file, { level: 'protected' });
  }

  static async uploadFile(
    key: string,
    file: File,
    options: Parameters<typeof Storage.put>[2],
    dispatch?: Dispatch,
    skipCredentialReset?: boolean,
  ) {
    const uploadFunction = () =>
      Storage.put(key, file, {
        ...options,
        contentType: file.type,
      });

    try {
      // If you remove the code before the uploadFunction below you can get a token expired issue if you
      // start a session, wait for an hour, and then try to upload a file.
      //
      // before uploading the file, we need to check if the user is still authenticated
      // https://github.com/aws-amplify/amplify-js/blob/aws-amplify%405.3.6/packages/auth/src/Auth.ts#L1702
      // The Storage put method should be handling this for us and based on reading their code
      // and console logs it does try to do it, but it doesn't seem to work as expected.
      // As a work-around we call this method to ensure that the credentials are refreshed.
      // Furthermore, this specific currentUserCredentials will set the Credentials singleton that is used by Storage.put.
      if (!skipCredentialReset) {
        // if the parent method calling this method already authenticated the user we can skip this step
        await Auth.currentAuthenticatedUser();

        // this is another stupid workaround. All the Storage functions except for put will reset the
        // credentials cached in config property of Storage class by calling _ensureCredentials.
        // https://github.com/aws-amplify/amplify-js/blob/aws-amplify%405.3.6/packages/storage/src/providers/AWSS3Provider.ts#L887
        // So we call a function we know will reset the credentials and then upload the file.
        // Forward the caller's `level` so the credential refresh targets the
        // correct access tier (e.g. "protected"). Without this, the default
        // "public" level can leave the cached credentials scoped incorrectly.
        try {
          await Storage.getProperties(key, { level: options?.level });
        } catch (ex) {
          // do nothing, this is expected to fail, we are just trying to reset credentials
        }
      }

      return await uploadFunction();
    } catch (err) {
      handleFileUploadFailure(err, dispatch);
      return null;
    }
  }

  static getFile(key: string, options = {}) {
    return Storage.get(key, options);
  }

  static listFiles(path: string, options = {}) {
    return Storage.list(path, { track: true, ...options });
  }

  static removeFile(key: string, options = {}) {
    return Storage.remove(key, options);
  }

  static getFileUrlWithSignedKey = async (
    key: string,
    signingRoute: string,
  ) => {
    const s3presignedUrl = await API.get('AppAPI', signingRoute, {
      queryStringParameters: {
        key,
      },
      responseType: 'text',
    });
    return s3presignedUrl;
  };

  static downloadFileWithSignedKey = async (
    key: string,
    signingRoute: string,
  ) => {
    const s3presignedUrl = await API.get('AppAPI', signingRoute, {
      queryStringParameters: {
        key,
      },
      responseType: 'text',
    });

    const config: AxiosRequestConfig = { responseType: 'blob' };
    return Axios.get(s3presignedUrl, config);
  };

  static async uploadFileWithSignedKey(
    file: File,
    key: string,
    signingRoute: string,
    progressCallback?: (loaded: number, total: number) => void,
    dispatch?: Dispatch,
  ): Promise<SignedUploadResult> {
    const s3presignedUrl = await API.get('AppAPI', signingRoute, {
      queryStringParameters: {
        key,
      },
      responseType: 'text',
    });

    try {
      await Axios.put(s3presignedUrl, file, {
        headers: {
          'Content-Type': file.type,
        },
        onUploadProgress: (progressEvent) => {
          if (progressCallback) {
            progressCallback(progressEvent.loaded, progressEvent.total);
          }
        },
      });
      return {
        // get base path without including the AWS generated signatures
        Location: s3presignedUrl.split('?')[0],
      };
    } catch (error) {
      handleFileUploadFailure(error, dispatch);
      return {
        Location: '',
      };
    }
  }

  static async uploadPortalFile(
    file: File,
    key: string,
    progressCallback?: (loaded: number, total: number) => void,
    dispatch?: Dispatch,
  ): Promise<SignedUploadResult> {
    const s3presignedUrl = await API.get('AppAPI', `/upload/signedUrl`, {
      queryStringParameters: {
        key,
        access: 'public',
      },
      responseType: 'text',
    });

    try {
      await Axios.put(s3presignedUrl, file, {
        headers: {
          'Content-Type': file.type,
        },
        onUploadProgress: (progressEvent) => {
          if (progressCallback) {
            progressCallback(progressEvent.loaded, progressEvent.total);
          }
        },
      });
      return {
        Location: s3presignedUrl.split('?')[0],
      };
    } catch (error) {
      handleFileUploadFailure(error, dispatch);
      return {
        Location: '',
      };
    }
  }

  // TODO: strongly type this function, it's been returning any[] for a long time
  static async uploadFilesToS3({
    acceptedFiles,
    dispatch,
    exitUploadFiles,
    filePathToS3,
    ignoreFolders = false,
    isUserFiles = true,
    modifiedFolderUploadBasePath = '',
    setUploadProgress,
    startUploadFiles,
    targetPath = '',
  }: UploadFilesToS3Input): Promise<any[]> {
    let files: Array<File> = [];
    if (acceptedFiles.target && acceptedFiles.target.files) {
      files = [...acceptedFiles.target.files];
    } else {
      files = acceptedFiles;
    }
    files = files.filter((f) => !f.name.startsWith('.'));
    const isoDate = new Date().toISOString();
    const path = filePathToS3 || `files/${isoDate}`;

    const loaded: Array<number> = files.map(() => 0);
    let totalSize = 0;

    if (files && files.length > 0) {
      totalSize = files.reduce((sum, { size }) => sum + size, 0);
    }

    if (totalSize > 0) {
      startUploadFiles();
    }

    try {
      // this is needed to ensure that the credentials are refreshed before beginning to upload an unknown number of files
      await Auth.currentUserCredentials();
      const firstFile = files.at(0);
      const keyComponents: string[] = [path];
      if (firstFile?.webkitRelativePath) {
        keyComponents.push(firstFile.webkitRelativePath);
      }
      keyComponents.push(firstFile?.name || '');
      const firstKey = `${keyComponents.join('/')}`;
      try {
        await Storage.getProperties(firstKey); // reset the credentials
      } catch (ex) {
        // do nothing, this is expected to fail, we are just trying to reset credentials
      }
      const uploadedFiles = await Promise.all(
        files.map(async (file, index) => {
          const fileKeyComponents: string[] = [path];
          if (file.webkitRelativePath) {
            // this property may look like "casper/icon.png" thus creating a file key like files/2021-08-25T13:53:09.443Z/casper/icon.png/icon.png
            // while it looks strange, this will ensure that file keys for files with the same name in different directories unique
            fileKeyComponents.push(file.webkitRelativePath);
          }
          fileKeyComponents.push(file.name);
          const filePath = `${fileKeyComponents.join('/')}`;
          const progressCallback = (progressData: ProgressData) => {
            // Check if loaded chunk has size greater than the last loaded chunk size
            // This check is needed to avoid final entry of S3 Multipart upload event that emits load chunk size less than that of total file size.
            loaded[index] =
              loaded[index] < progressData.loaded
                ? progressData.loaded
                : loaded[index];

            setUploadProgress({
              totalSize,
              loaded,
            });
          };

          // when files we're going to upload are
          // belonging to unique user, we need to store
          // them in the protected s3 bucket, otherwise
          // we store them in the public s3 bucket
          return !isUserFiles
            ? this.uploadPortalFile(
                file,
                `${targetPath}/${file.name}`,
                (progress, total) => {
                  progressCallback({ loaded: progress, totalSize: total });
                },
                dispatch,
              )
            : this.uploadFile(
                filePath,
                file,
                {
                  level: 'protected',
                  progressCallback,
                },
                dispatch,
                true,
              );
        }),
      );

      const hasFailures = uploadedFiles.some((file) => {
        if (file === null) return true;
        // uploadFile (isUserFiles=true) returns object with 'key'
        if ('key' in file) return !file.key;
        // uploadPortalFile (isUserFiles=false) returns object with 'Location'
        if ('Location' in file) return !file.Location;
        return true;
      });

      if (hasFailures) {
        // Note: Only handles user files (isUserFiles=true) which have 'key' property.
        // Portal files (isUserFiles=false) use 'Location' URL and would need different handling.
        const successfulKeys = uploadedFiles
          .filter(
            (file): file is { key: string } =>
              file !== null && 'key' in file && !!file.key,
          )
          .map((file) => file.key);
        const successfulPortalFiles = uploadedFiles.filter(
          (file): file is { Location: string } =>
            file !== null && 'Location' in file && !!file.Location,
        );
        const totalSuccessful =
          successfulKeys.length + successfulPortalFiles.length;

        const failureContext = {
          fileCount: files.length,
          failedCount: files.length - totalSuccessful,
          totalSizeBytes: totalSize,
          fileTypes: files.map((f) => f.type),
          isUserFiles,
        };
        // Note: individual file failures also fire handleFileUploadFailure events.
        // This batch event provides aggregate context (counts, sizes) that individual events don't.
        Sentry.captureMessage('S3 batch upload partially failed', {
          level: 'error',
          tags: { feature: 'file_upload' },
          extra: failureContext,
        });
        posthog.capture('file_upload_batch_failed', failureContext);

        console.error(
          'Some files failed to upload. Files:',
          files,
          'Successful keys:',
          successfulKeys,
        );

        try {
          // Note: this won't finish if the user navigates away. Some files may still be orphaned.
          await Promise.all(successfulKeys.map((key) => this.removeFile(key)));
        } catch (error) {
          console.error(
            'Error removing files:',
            successfulKeys,
            'Error:',
            error,
          );
        }

        if (dispatch) {
          notify({
            status: 'error',
            errorMessage: 'File upload failed. Please try again.',
            dispatch,
          });
        }
        exitUploadFiles();
        return [];
      }

      const folderPaths: string[] = getFilePathList(
        files,
        modifiedFolderUploadBasePath,
        ignoreFolders,
      );
      return getFileMetadataObjects(uploadedFiles, folderPaths, targetPath);
    } catch (err) {
      const error = ensureError(err);
      reportException(error, {
        tags: { feature: 'file_upload' },
        extra: {
          fileCount: files.length,
          totalSizeBytes: totalSize,
          isUserFiles,
        },
      });
      posthog.capture('file_upload_failed', {
        error_message: error.message,
        error_name: error.name,
        fileCount: files.length,
        totalSizeBytes: totalSize,
      });
      if (dispatch) {
        notify({
          status: 'error',
          errorMessage: 'File upload failed. Please try again.',
          dispatch,
        });
      }
      exitUploadFiles();
      return [];
    }
  }

  static getBlobFromS3Url = async (url: string) => {
    try {
      // Fetch the file from S3
      const response = await fetch(url);

      // Check if the request was successful
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }

      // Convert the file to a Blob
      const blob = await response.blob();

      // Create a Blob URL
      const blobUrl = URL.createObjectURL(blob);

      return blobUrl;
    } catch (error) {
      console.error('Error creating blob URL:', error);
      return null;
    }
  };
}
